import { Checkbox, Collapse, Space, Switch, Table } from "antd"
import VSpace from "../../common/VSpace"
import ExpandIcon from "../../common/ExpandIcon"
import { VisitSelector } from "../../Items/VisitSelector"
import { roundToPrecision } from "../../../helpers/math"
import { InvoiceItemRow, filterWorkareaItems, mapRows } from "../../Items/InvoiceItems/invoiceItemsModel"
import { ServerState, ViewItemFilter, createViewItems } from "../../Items/itemModel"
import { WorkareaItemTypes } from "../../../common/constants"
import { useContext, useEffect, useState } from "react"
import { ItemsContext } from "../../Items/ItemsRoot"
import { groupItems } from "../../Items/grouping"
import { hasValue } from "../../../helpers/nullable"
import { centsToDollarsDisplay } from "../../displayFormatters"
import Units from "../../Items/Units"
import { sum } from "../../../helpers/array"
import { Action } from "./createInvoiceReducer"
import { InvoiceWorkareaGroup } from "./createInvoiceModel"

type Props = {
  workareaGroup: InvoiceWorkareaGroup
  invoiceDispatch: (action: Action) => void
}

const ActualItemSelector = ({ workareaGroup, invoiceDispatch }: Props) => {
  const { changeSet, jobItemsQuery, jobData, dispatch } = useContext(ItemsContext)

  const workareaIds = jobData.workareas.filter(x => x.workareaGroupId === workareaGroup.id).map(x => x.id)

  const workareaItems = filterWorkareaItems((jobItemsQuery.data?.actualItems.filter(x => workareaIds.includes(x.workareaId)) ?? []))
  useEffect(() => {
    setSelected(workareaItems.map(x => x.guid))
  }, [workareaItems.length])

  const serverState: ServerState = {
    workareaItems,
    catalogItems: jobItemsQuery.data?.catalogItems ?? [],
    templateItems: [],
    estimatedQuantities: {}
  }

  const [expandedRows, setExpandedRows] = useState<string[]>([])
  const [selectedVisitIds, setSelectedVisitIds] = useState<string[]>([])
  useEffect(() => {
    const visitIds = new Set(workareaItems.map(x => x.visitId).filter(x => hasValue(x)) as string[])
    setSelectedVisitIds(jobData.visitData.filter(x => visitIds.has(x.id)).map(x => x.id))
  }, [jobData.visitData, workareaItems.length])

  const filter: ViewItemFilter = {
    workareaIds,
    workareaItemType: WorkareaItemTypes.Actual
  }
  const viewItems = createViewItems(changeSet, serverState, jobData.visitData, jobData.budgets, true, jobData.workareas, filter)
  const groups = groupItems([], viewItems, serverState.catalogItems, jobData.workareas)
  const rows = mapRows(groups, expandedRows)

  const groupItemGuids = Object.fromEntries(groups.map(g => [g.key, g.actualItems.map(x => x.guid)]))
  const selectedItemGuids = workareaGroup.selectedItemGuids

  const toggleRow = (key: string) => {
    if (expandedRows.includes(key)) {
      setExpandedRows(expandedRows.filter(x => x !== key))
    } else {
      setExpandedRows([...expandedRows, key])
    }
  }

  const isRowSelected = (item: InvoiceItemRow) => {
    if (item.isChildRow) {
      return selectedItemGuids.includes(item.key)
    }

    return groupItemGuids[item.key].every(x => selectedItemGuids.includes(x))
  }

  const isRowPartiallySelected = (item: InvoiceItemRow): boolean => {
    if (item.isChildRow) {
      return false
    }

    const groupSelectedCount = groupItemGuids[item.key].filter(x => selectedItemGuids.includes(x)).length
    return groupSelectedCount !== groupItemGuids[item.key].length && groupSelectedCount > 0
  }

  const onSelect = (item: InvoiceItemRow) => {
    if (item.isChildRow) {
      if (selectedItemGuids.includes(item.key)) {
        setSelected(selectedItemGuids.filter(x => x !== item.key))
      } else {
        setSelected([...selectedItemGuids, item.key])
      }
    } else {
      if (groupItemGuids[item.key].every(x => selectedItemGuids.includes(x))) {
        setSelected(selectedItemGuids.filter(x => !groupItemGuids[item.key].includes(x)))
      } else {
        setSelected(Array.from(new Set([...selectedItemGuids, ...groupItemGuids[item.key]])))
      }
    }
  }

  const setSelected = (itemGuids: string[]) => {
    const selectedVisitLaborIds = viewItems
      .filter(x => x.isFromTimesheets && itemGuids.includes(x.guid))
      .map(x => x.visit!.id)
    const selectedItemIds = viewItems
      .filter(x => !x.isFromTimesheets && itemGuids.includes(x.guid))
      .map(x => x.id)
    const invoiceSelectedPrice = sum(viewItems.filter(x => itemGuids.includes(x.guid)), x => x.totalPrice)
    invoiceDispatch({ type: 'SELECTED_ITEMS_CHANGED', groupId: workareaGroup.id, selectedItemGuids: itemGuids, selectedItemIds, selectedVisitLaborIds, amount: invoiceSelectedPrice })
  }
  const selectedRowKeys = [
    ...selectedItemGuids,
    ...groups.filter(g => g.actualItems.every(x => selectedItemGuids.includes(x.guid))).map(x => x.key)
  ]

  const onVisitIdsChange = (ids: string[]) => {
    const addedVisitIds = ids.filter(x => !selectedVisitIds.includes(x))
    const removedVisitIds = selectedVisitIds.filter(x => !ids.includes(x))

    const addedItemGuids = viewItems.filter(x => addedVisitIds.includes(x.visit?.id ?? '')).map(x => x.guid)
    const removedItemGuids = viewItems.filter(x => removedVisitIds.includes(x.visit?.id ?? '')).map(x => x.guid)

    setSelected([
      ...selectedItemGuids.filter(x => !removedItemGuids.includes(x)),
      ...addedItemGuids
    ])

    setSelectedVisitIds(ids)
  }

  return <Collapse
    items={[
      {
        key: 'actualItems',
        label: 'Choose items to invoice',
        children: <VSpace>
          <VisitSelector
            visitData={jobData.visitData}
            viewItems={viewItems}
            selectedVisitIds={selectedVisitIds}
            onChange={onVisitIdsChange} />
          <Table
            dataSource={rows}
            columns={[
              {
                key: 'expand',
                render: (_, item) => {
                  if (!item.canExpand) {
                    return <></>
                  }
                  return <Space>
                    <ExpandIcon
                      isExpanded={expandedRows.includes(item.key)}
                      onClick={() => toggleRow(item.key)}
                    />
                  </Space>
                },
                align: 'right',
                width: '5%',
              },
              {
                dataIndex: 'name',
                title: 'Name',
                render: (name, item) => {
                  return <Space>
                    {name}
                    {(item.numberOfEntries ?? 0) > 1 && <span style={{ fontWeight: 'initial' }}>({item.numberOfEntries})</span>}
                  </Space>
                },
                width: '35%'
              },
              {
                dataIndex: 'origin',
                title: 'Origin',
                render: (origin, item) => {
                  return origin && <div>
                    <span>{origin}</span><br />
                    <small style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>added by {item.originOwner}</small>
                  </div>
                },
                width: '25%',
                align: 'center'
              },
              {
                dataIndex: 'quantity',
                title: 'Quantity',
                render: (quantity, item) => <>{`${roundToPrecision(quantity, 2)} `}<Units units={item.units} /></>,
                align: 'right',
                width: '10%'
              },
              {
                dataIndex: 'isTaxable',
                title: 'Taxable',
                render: (isTaxable, item) => {
                  if (item.itemGuid && isTaxable !== undefined) {
                    return <Switch
                      checked={isTaxable}
                      onChange={(value) => dispatch({
                        type: 'TAXABLE_CHANGED',
                        guid: item.itemGuid!,
                        isVisitReviewed: false,
                        serverState,
                        value
                      })}
                    />
                  }

                  return <></>
                },
                width: '10%',
                align: 'center'
              },
              {
                dataIndex: 'totalPrice',
                title: 'Total Price',
                render: centsToDollarsDisplay,
                align: 'right',
                width: '10%'
              }
            ]}
            rowKey="key"
            onRow={item => ({
              style: {
                fontWeight: item.isChildRow ? 'normal' : '600'
              }
            })}
            rowSelection={{
              selectedRowKeys,
              renderCell: (_, item) => {
                const checked = isRowSelected(item)
                const indeterminate = isRowPartiallySelected(item)
                return <Checkbox checked={checked} indeterminate={indeterminate} onChange={() => onSelect(item)} />
              },
              onSelectAll: (checked) => {
                if (checked) {
                  setSelected(workareaItems.map(x => x.guid))
                } else {
                  setSelected([])
                }
              },
              columnWidth: '5%'
            }}
            pagination={false}
          />
        </VSpace>
      }
    ]}
  />
}

export default ActualItemSelector
